import React from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Chip,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Typography,
} from "@mui/material";
import useScrollTrigger from "@mui/material/useScrollTrigger";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";

import HeaderWhite from "../../media/shieldshot_header_white.png";

const isBeta = process.env.NODE_ENV !== "production";

// Mobile Navigation Drawer Component
function MobileMenu({ open, onClose, isAuthenticated }) {
  const navigate = useNavigate();
  
  const menuItems = [
    { text: "Home", href: "/" },
    { text: "About", href: "/about" },
    { text: "Pricing", href: "/pricing" },
    { text: "Roadmap", href: "/roadmap" },
  ];

  const handleMenuClick = (href) => {
    onClose();
    // Use React Router navigation for faster client-side routing
    navigate(href);
  };

  const handleAuthNavigation = (path) => {
    onClose();
    navigate(path);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: 280,
          backgroundColor: "#2997ff",
          color: "white",
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h6" sx={{ color: "white", fontWeight: 700 }}>
            Menu
          </Typography>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>
        
        <List>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton onClick={() => handleMenuClick(item.href)}>
                <ListItemText 
                  primary={item.text} 
                  sx={{ color: "white" }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        
        <Divider sx={{ my: 2, backgroundColor: "rgba(255,255,255,0.2)" }} />
        
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {!isAuthenticated && (
            <Button
              variant="outlined"
              fullWidth
              sx={{
                color: "white",
                borderColor: "white",
                borderRadius: "20px",
              }}
              onClick={() => handleAuthNavigation("/login")}
            >
              Sign In
            </Button>
          )}
          <Button
            variant="contained"
            fullWidth
            sx={{
              background: "white",
              color: "#2997ff",
              borderRadius: "20px",
              fontWeight: 700,
              boxShadow: 0,
              "&:hover": {
                background: "rgba(255,255,255,0.9)",
              },
            }}
            onClick={() => {
              localStorage.clear();
              handleAuthNavigation("/login?mode=signup");
            }}
          >
            Sign Up
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

function ElevationScroll(props) {
  const { children } = props;
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 60,
  });

  return React.cloneElement(children, {
    elevation: trigger ? 4 : 0,
    sx: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      zIndex: 1200,
      boxShadow: trigger ? 3 : 0,
      background: "#2997ff",
      transition: "all 0.3s",
      minHeight: trigger ? { xs: 48, sm: 56, md: 72 } : { xs: 48, sm: 56, md: 72 },
      px: { xs: 0, md: 0 },
    },
  });
}

function Navigation({ isAuthenticated }) {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const authenticated = isAuthenticated || false;

  const handleNavigation = (href) => {
    navigate(href);
  };

  const handleAuthNavigation = (path) => {
    navigate(path);
  };

  return (
    <>
      {/* Header/NavBar */}
      <ElevationScroll>
        <AppBar color="primary" elevation={0}>
          <Toolbar
            sx={{
              justifyContent: "space-between",
              px: { xs: 1, sm: 2, md: 8 },
              minHeight: { xs: 48, sm: 56, md: 72 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                component="img"
                src={HeaderWhite}
                alt="ShieldShot"
                onClick={() => handleNavigation("/")}
                sx={{
                  height: { xs: "1.5rem", sm: "1.8rem", md: "2.2rem" },
                  mr: { xs: 0.5, sm: 0.75, md: 1 },
                  transition: "height 0.3s",
                  cursor: "pointer",
                }}
              />
              {isBeta && (
                <Chip
                  label="Beta"
                  color="primary"
                  size="small"
                  sx={{
                    ml: { xs: 0.5, sm: 0.75, md: 1 },
                    background: "white",
                    color: "#2997ff",
                    fontWeight: 700,
                    fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.75rem" },
                    height: { xs: 20, sm: 24, md: 32 },
                  }}
                />
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: { xs: 0.5, sm: 1, md: 3 },
                alignItems: "center",
              }}
            >
              {/* Navigation links - hidden on mobile */}
              <Box
                sx={{
                  display: { xs: "none", md: "flex" },
                  gap: { md: 2 },
                  alignItems: "center",
                }}
              >
                <Button
                  sx={{ 
                    color: "white", 
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    px: 2,
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },
                  }}
                  disableRipple
                  onClick={() => handleNavigation("/")}
                >
                  Home
                </Button>
                <Button
                  sx={{ 
                    color: "white", 
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    px: 2,
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },
                  }}
                  disableRipple
                  onClick={() => handleNavigation("/about")}
                >
                  About
                </Button>
                <Button
                  sx={{ 
                    color: "white", 
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    px: 2,
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },
                  }}
                  disableRipple
                  onClick={() => handleNavigation("/pricing")}
                >
                  Pricing
                </Button>
                <Button
                  sx={{ 
                    color: "white", 
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    px: 2,
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },
                  }}
                  disableRipple
                  onClick={() => handleNavigation("/roadmap")}
                >
                  Roadmap
                </Button>
              </Box>
              
              {/* Desktop Auth buttons */}
              <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1, alignItems: "center" }}>
                {!authenticated && (
                  <Button
                    variant="outlined"
                    sx={{
                      color: "white",
                      borderColor: "white",
                      borderRadius: "20px",
                      fontSize: "0.875rem",
                      px: 2,
                      py: 1,
                      "&:hover": {
                        borderColor: "white",
                        backgroundColor: "rgba(255,255,255,0.1)",
                      },
                    }}
                    onClick={() => handleAuthNavigation("/login")}
                  >
                    Sign In
                  </Button>
                )}
                <Button
                  variant="contained"
                  sx={{
                    background: "white",
                    color: "#2997ff",
                    borderRadius: "20px",
                    fontWeight: 700,
                    boxShadow: 0,
                    fontSize: "0.875rem",
                    px: 2,
                    py: 1,
                    "&:hover": {
                      background: "rgba(255,255,255,0.9)",
                      boxShadow: 1,
                    },
                  }}
                  onClick={() => {
                    localStorage.clear();
                    handleAuthNavigation("/login?mode=signup");
                  }}
                >
                    Sign Up
                </Button>
              </Box>

              {/* Mobile/Tablet Auth buttons and menu */}
              <Box sx={{ display: { xs: "flex", md: "none" }, gap: 1, alignItems: "center" }}>
                {!authenticated && (
                  <Button
                    variant="outlined"
                    sx={{
                      color: "white",
                      borderColor: "white",
                      borderRadius: "20px",
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      px: { xs: 1, sm: 1.5 },
                      py: { xs: 0.5, sm: 0.75 },
                      minWidth: "auto",
                    }}
                    onClick={() => handleAuthNavigation("/login")}
                  >
                    Sign In
                  </Button>
                )}
                <Button
                  variant="contained"
                  sx={{
                    background: "white",
                    color: "#2997ff",
                    borderRadius: "20px",
                    fontWeight: 700,
                    boxShadow: 0,
                    fontSize: { xs: "0.7rem", sm: "0.8rem" },
                    px: { xs: 1, sm: 1.5 },
                    py: { xs: 0.5, sm: 0.75 },
                    minWidth: "auto",
                  }}
                  onClick={() => {
                    localStorage.clear();
                    handleAuthNavigation("/login?mode=signup");
                  }}
                >
                  Sign Up
                </Button>
                
                {/* Mobile menu button */}
                <IconButton
                  sx={{ 
                    color: "white",
                    display: { xs: "flex", md: "none" },
                    ml: 1,
                  }}
                  onClick={() => setMobileMenuOpen(true)}
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>
      </ElevationScroll>
      
      {/* Mobile Navigation Drawer */}
      <MobileMenu
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        isAuthenticated={authenticated}
      />
      
      {/* Spacer for sticky header */}
      <Box sx={{ minHeight: { xs: 48, sm: 56, md: 72 } }} />
    </>
  );
}

export default Navigation;
